import Skeleton from 'react-loading-skeleton';
import { AIECGViewer, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { getSelectedEventType } from '@/entities/event';
import { BEAT_HR_TAB_ID } from '@/entities/report';
import useEventEcgViewerWrapper from './useEventEcgViewerWrapper';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
};

export type EventEcgViewerWrapperProps = {
  ecgDataMap: any;
  profileId: string;
  studyId: string;
  timezoneOffset: number;
  handleClickArrowButton: (isNext: boolean) => void;
};

/**
 * Hosts the ECG viewer for the selected event in the artifact areas view. Shows a three-part skeleton until the ECG object, its samples and the strip start epoch are ready, then mounts the viewer with beat-HR canvas ids, event and bookmark indicators, capture, pick date and beat update handlers and the study context.
 *
 * @param props - The viewer props, carrying ecgDataMap, studyId, profileId and timezoneOffset.
 * @returns The rendered ECG viewer, or its skeleton while data loads.
 */
const EventEcgViewerWrapper = ({
  ecgDataMap,
  profileId,
  studyId,
  timezoneOffset,
  handleClickArrowButton,
}: EventEcgViewerWrapperProps) => {
  const {
    isLoading,
    isLoadingStrip,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    isPickingDate,
    selectedEvent,
    getCaptureMode,
    handleCaptureImages,
    handlePickDate,
    clickArrowButtonHandle,
    handleUpdateBeat,
  } = useEventEcgViewerWrapper({
    ecgDataMap,
    profileId,
    studyId,
    timezoneOffset,
    handleClickArrowButton,
  });

  if (isLoading || isLoadingStrip || !ecgObject?.dataSamples?.length || !startStripEpoch.current) {
    return (
      <div id="ecg-viewer" className="ecg-viewer-container">
        <div className="ecg-viewer-skeleton-container">
          <div className="scroller-container">
            <Skeleton height="100%" />
          </div>
          <div className="chart-container">
            <Skeleton height="100%" />
          </div>
          <div className="menu-container">
            <Skeleton height="100%" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ecg-viewer" className="ecg-viewer-container">
      <AIECGViewer
        ref={ecgChartRef}
        idComponents={{
          canvasBackground: 'cvBgBeatHr',
          exportGridCanvas: 'expGridCvBeatHr',
          canvasChart: 'cvChartBeatHr',
          canvasObject: 'cvObjBeatHr',
          canvasScroller: 'cvScrollerBeatHr',
          contextMenu: 'contextMenuBeatHr',
          tooltip: 'tooltipBeatHr',
          tab: BEAT_HR_TAB_ID,
        }}
        ecg={ecgObject}
        ecgDataMap={ecgDataMap}
        configChart={configChart}
        isAppliedAI
        isDisableAIAssist
        isHideFineGridMenu
        isHideMarkASampleMenu
        hasUpdateBeatContextMenu
        captureMode={getCaptureMode()}
        handleCaptureImages={handleCaptureImages}
        handlePickDate={handlePickDate}
        pickDateAction={pickDateAction}
        eventIndicator={eventIndicator}
        bookmarkIndicators={bookmarkIndicators}
        hasArrowButtonScroller
        handleClickArrowButton={clickArrowButtonHandle}
        handleUpdateBeat={handleUpdateBeat}
        timeObject={{
          startMoment: startStripEpoch.current,
          timezoneOffset,
        }}
        isInitialARBeatMode={!isPickingDate && ECG_VIEWER_CONFIG_STORE.isInitialARBeatMode}
        aiChannel={aiChannel}
        isDisableNextBtn={isDisableNextBtn}
        isDisablePrevBtn={isDisablePrevBtn}
        studyInfoObject={{
          studyId,
          profileId,
          type: getSelectedEventType(selectedEvent),
        }}
      />
    </div>
  );
};

export default EventEcgViewerWrapper;
